import React, { useEffect, useState } from 'react'
import {
	Alert, Box, Card, CardContent, MenuItem, Table, TableBody, TableCell,
	TableHead, TableRow, TextField, Typography,
} from '@mui/material'

import {
	IContractUsage, formatDay, formatEur, formatGb, formatHits, getContractUsage,
} from '../../../Services/contractDates'

interface Props {
	billingAccountId: string
	/** Rechargé quand les dates changent : la période affichée en dépend. */
	reloadKey?: string
}

// La consommation d'un compte sous contrat sur une année.
//
// ⚠️ Aucun inclus, aucun montant facturé — rien ici ne produit de facture. Un
// compte sous contrat ne se voit poser aucune ligne de dépassement : le
// commercial lit ces volumes et négocie. Les euros affichés sont les COÛTS GCP,
// c'est-à-dire ce que Quanti paie à Google. Ils ne sortent jamais de l'admin.

// #region ContractUsageCard
export const ContractUsageCard: React.FC<Props> = ({ billingAccountId, reloadKey }) => {
	const [usage, setUsage] = useState<IContractUsage | null>(null)
	const [period, setPeriod] = useState('')

	useEffect(() => {
		if (!billingAccountId) return
		getContractUsage(billingAccountId, period)
			.then(setUsage)
			.catch(() => setUsage(null))
	}, [billingAccountId, period, reloadKey])

	// ⚠️ `periods` peut arriver à `null` d'un api-billing antérieur au correctif
	// (slice Go nil sérialisée) : un compte sans dates de contrat plantait la fiche.
	if (!usage?.periods?.length) return null

	return (
		<Card style={{ marginBottom: 20, width: '100%' }}>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Consommation de l’année
				</Typography>

				<TextField
					select
					size="small"
					value={usage.period}
					onChange={(e) => setPeriod(e.target.value)}
					sx={{ minWidth: 240, mb: 1 }}
				>
					{usage.periods.map((p) => (
						<MenuItem key={p} value={p}>
							à partir du {formatDay(p)}
						</MenuItem>
					))}
				</TextField>

				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
					{formatDay(usage.periodStart)} → {formatDay(usage.periodLastDay)} ({usage.periodDays} jours)
					{usage.totalCostEur !== undefined && <> · coût Google&nbsp;: <strong>{formatEur(usage.totalCostEur)}</strong></>}
				</Typography>

				{/* ⚠️ Une année en cours n'est pas comparable à une année close.
				    Sans le dire, un volume plus faible se lit comme une baisse
				    d'usage alors que l'année n'est qu'à moitié écoulée. */}
				{usage.ongoing && (
					<Alert severity="info" sx={{ mb: 1.5 }}>
						Année en cours : ces volumes sont partiels et ne se comparent pas à une année complète.
					</Alert>
				)}

				{usage.projects.length > 0 && (
					<Table size="small" sx={{ mb: 2 }}>
						<TableHead>
							<TableRow>
								<TableCell>Projet managé</TableCell>
								<TableCell align="right">Analysé (cumul)</TableCell>
								{/* ⚠️ MOYENNE et non cumul : le stockage est un stock,
								    facturé en Go-mois. Cumulé sur douze mois, 400 Go
								    stockés s'afficheraient « 4 800 Go ». */}
								<TableCell align="right">Stocké (moyenne)</TableCell>
								<TableCell>Services Google</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{usage.projects.map((p) => (
								<TableRow key={p.folderId}>
									<TableCell>
										{p.name}
										{/* ⚠️ Un écart de journées dit « pas mesuré », jamais
										    « rien consommé » — sur une année, la nuance vaut
										    une facture. */}
										{p.measuredDays < usage.periodDays && (
											<Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
												{p.measuredDays} jour(s) mesuré(s) sur {usage.periodDays}
											</Typography>
										)}
									</TableCell>
									<TableCell align="right">{formatGb(p.analysisGb)}</TableCell>
									<TableCell align="right">{formatGb(p.storageAvgGb)}</TableCell>
									<TableCell>
										{p.services.length === 0 && <Typography variant="caption" color="text.secondary">—</Typography>}
										{p.services.map((s) => (
											<Typography key={s.service} variant="caption" sx={{ display: 'block' }}>
												{s.service}
												{s.costEur !== undefined && <> — {formatEur(s.costEur)}</>}
											</Typography>
										))}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}

				{usage.servers.length > 0 && (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Serveur Track</TableCell>
								<TableCell align="right">Hits (cumul)</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{usage.servers.map((s) => (
								<TableRow key={s.id}>
									<TableCell>{s.domain}</TableCell>
									{/* ⚠️ « non mesuré » et surtout pas « 0 » : un serveur
									    provisionné avant le sidecar de comptage ne remonte
									    aucun hit, et zéro le ferait passer pour un serveur
									    sans trafic. */}
									<TableCell align="right">
										{s.hits === null ? (
											<Typography variant="caption" color="warning.main">non mesuré</Typography>
										) : (
											formatHits(s.hits)
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}

				{usage.projects.length === 0 && usage.servers.length === 0 && (
					<Typography variant="caption" color="text.secondary">
						Aucun projet managé ni serveur Track sur ce compte.
					</Typography>
				)}

				<Box sx={{ mt: 1.5 }}>
					<Typography variant="caption" color="text.secondary">
						Les montants sont ce que Quanti paie à Google, jamais ce qui est facturé au client.
						Aucun dépassement n’est calculé pour un compte sous contrat.
					</Typography>
				</Box>
			</CardContent>
		</Card>
	)
}

// #endregion
